import React from 'react';
import {
  Grid,
  PagingPanel,
  Table,
  TableEditRow,
  TableHeaderRow,
  TableSelection,
} from '@devexpress/dx-react-grid-material-ui';
import {
  CustomPaging,
  DataTypeProvider,
  EditingState,
  IntegratedSelection,
  PagingState,
  SelectionState,
  TableColumnWidthInfo,
} from '@devexpress/dx-react-grid';
import {
  Button,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from '@material-ui/core';
import GetAppIcon from '@material-ui/icons/GetApp';
import {
  DataTypeEditors,
  DataTypeFormatters,
} from 'components/atoms/CustomGridComponents/DataTypeProvider';
import dayjs from 'dayjs';
import { Hide } from 'components';
import { useAuth } from 'hooks/useAuth';
import { GROUPS } from 'constants/permissions';
import Loading from 'components/atoms/Loading';
import {
  getGroupOptions,
  putGroupPermissionGrid,
} from 'services/msgraph-group';
import { useQuery } from 'react-query';
import { debounce } from 'lodash';

import useStyles from './style';
import {
  IGrid,
  ICustomChangeSet,
  IOption,
  IValidationRules,
  IRow,
} from './types';

const pagingPanelMessages = {
  showAll: 'Todos',
  rowsPerPage: 'Linhas por página',
  info: ({ from, to, count }) => `${from}-${to} de ${count}`,
};

const tableMessages = {
  noData: 'Sem dados disponíveis',
};

const GroupPermissionGrid = ({
  columns,
  rows,
  getRowId,
  getRowById,
  dateTimeCells = [],
  currentPage,
  pageSize,
  pageSizes,
  totalCount,
  onCurrentPageChange,
  onPageSizeChange,
  loading,
  validationRules,
  onExport,
  refetch,
}: IGrid) => {
  const classes = useStyles();
  const { checkPermissions } = useAuth();

  const [selection, setSelection] = React.useState<(string | number)[]>([]);
  const [editingRowIds, setEditingRowIds] = React.useState<(string | number)[]>(
    [],
  );
  const [isEditing, setIsEditing] = React.useState<boolean>(false);
  const [rowChanges, setRowChanges] = React.useState({});
  const [errors, setErrors] = React.useState<IValidationRules>({});

  const [groupOptions, setGroupOptions] = React.useState<IOption[]>([]);
  const [updatingGridData, setUpdatingGridData] =
    React.useState<boolean>(false);

  const queryGroupOptions = useQuery({
    queryKey: ['GroupOptionsContext'],
    queryFn: async () => {
      const { data } = await getGroupOptions();
      return data;
    },
    select: data => {
      return data.map(option => {
        return {
          label: option.group,
          value: option.id,
        };
      });
    },
    onSuccess: (data: IOption[]) => {
      setGroupOptions(data);
    },
    enabled: true,
  });

  const tableColumnExtensions = React.useMemo<TableColumnWidthInfo[]>(
    () =>
      columns.map(column => {
        return {
          columnName: column.name,
          width: column.width,
          wordWrapEnabled: true,
          align: column.align,
        } as TableColumnWidthInfo;
      }),
    [columns],
  );

  const disableEditingButton = React.useMemo(
    // a linha só é habilitada quando tem 1 ou mais linhas selecionadas
    () => !selection?.length,
    [selection?.length],
  );

  const disableCancelButton = React.useMemo(() => {
    return !isEditing && !selection?.length;
  }, [isEditing, selection?.length]);

  const canSave = React.useMemo(() => {
    // se esta editando E NÃO se existe algum erro em alguma linha
    return (
      isEditing &&
      !Object.values(errors).some(rowId =>
        Object.values(rowId).some(row => !row.isValid),
      )
    );
  }, [errors, isEditing]);

  const editingStateColumnExtensions = React.useMemo(
    () =>
      columns.map(column => {
        return {
          columnName: column.name,
          editingEnabled: Boolean(column?.editingEnabled),
        };
      }),
    [columns],
  );

  const renderTableSelectionRowComponent = React.useCallback(
    props => {
      const { highlighted } = props;

      return (
        <Table.Row
          {...props}
          className={highlighted ? classes.selectedRows : null}
        />
      );
    },
    [classes.selectedRows],
  );

  const renderTitleComponent = React.useCallback(
    props => <TableHeaderRow.Title className={classes.tableTitle} {...props} />,
    [classes.tableTitle],
  );

  const CellDateTime = React.useCallback(
    props => {
      const formattField = value =>
        DataTypeFormatters.DateCellFormatter({
          value: dayjs(value).toString(),
          format: 'DD/MM/YY HH:mm',
        });

      return (
        <DataTypeProvider
          for={dateTimeCells}
          formatterComponent={({ value }) => formattField(value)}
          editorComponent={({ value }) => formattField(value)}
          {...props}
        />
      );
    },
    [dateTimeCells],
  );

  const GroupFormatter = React.useCallback(
    ({ value }) => {
      return (
        groupOptions
          .filter(item => value.includes(item.value))
          .map(item => item.label)
          .join(', ') || 'Sem grupo'
      );
    },
    [groupOptions],
  );

  const CellUserGroup = React.useCallback(
    props => (
      <DataTypeProvider
        for={['group']}
        formatterComponent={GroupFormatter}
        editorComponent={({ column, row, value, disabled, onValueChange }) => {
          if (disabled) return value;

          const rowId = getRowId(row);
          const hasError =
            Boolean(errors[rowId]?.[column.name]) &&
            !errors[rowId]?.[column.name]?.isValid;

          return (
            <DataTypeEditors.MultipleSelectOptionsEditorComponent
              options={groupOptions}
              error={hasError}
              value={value}
              isLoading={queryGroupOptions?.isLoading}
              onValueChange={onValueChange}
            />
          );
        }}
        {...props}
      />
    ),
    [
      GroupFormatter,
      errors,
      getRowId,
      groupOptions,
      queryGroupOptions?.isLoading,
    ],
  );

  const commitChanges = React.useCallback(
    ({ changed }: ICustomChangeSet) => {
      const edited = Object.entries(changed || {}).map(([rowId, rowValues]) => {
        const onlyEditableColumns = editingStateColumnExtensions
          .filter(column => column.editingEnabled)
          .map(column => column.columnName);

        const filtered = Object.fromEntries(
          Object.entries(rowValues).filter(([key]) =>
            onlyEditableColumns.includes(key),
          ),
        );

        return {
          userId: Number(rowId),
          groupIds: filtered?.group,
        };
      });

      setUpdatingGridData(true);
      putGroupPermissionGrid(edited)
        .then(() => {
          if (refetch) refetch();
        })
        .finally(() => setUpdatingGridData(false));
    },
    [editingStateColumnExtensions, refetch],
  );

  const RowEditingComponent = React.useCallback(
    props => {
      return <Table.Row {...props} className={classes.selectedRows} />;
    },
    [classes.selectedRows],
  );

  const CellEditingComponent = React.useCallback(
    props => {
      const {
        tableRow: { rowId },
        column: { name: columnName },
      } = props;
      const columnStatus = errors[rowId]?.[columnName];
      const valid = !columnStatus || columnStatus.isValid;
      const title = valid ? '' : errors[rowId][columnName].error;

      return <Table.Cell {...props} title={title} />;
    },
    [errors],
  );

  const validate = React.useCallback(
    (editedRows: { [key: string]: IRow }): IValidationRules => {
      return Object.entries(editedRows).reduce((acc, [rowId, partialRow]) => {
        const validatedFields = {};
        Object.entries(partialRow).forEach(([colName, colValue]) => {
          if (validationRules && validationRules[colName]) {
            const row = {
              ...(getRowById(rowId) || ({} as IRow)),
              ...partialRow,
            };
            validatedFields[colName] = {
              isValid: validationRules[colName].isValid(colValue, row),
              error: validationRules[colName].errorText,
            };
          }
        });

        return {
          ...acc,
          [rowId]: validatedFields,
        };
      }, {});
    },
    [getRowById, validationRules],
  );

  const onEdited = React.useMemo(
    () =>
      debounce(edited => {
        const rowsValidated = validate(edited);

        setErrors(rowsValidated);
      }, 250),
    [validate],
  );

  const cancelEditingAction = React.useCallback(() => {
    setEditingRowIds([]);
    setIsEditing(false);
    setSelection([]);
    setRowChanges([]);
  }, []);

  const editingSelectedColumns = React.useCallback(() => {
    if (isEditing) {
      if (Object.keys(rowChanges).length > 0) {
        commitChanges({ changed: rowChanges });
      }
      setEditingRowIds([]);
      setIsEditing(false);
      setSelection([]);
    } else {
      setEditingRowIds(selection);
      setIsEditing(true);
    }
  }, [commitChanges, isEditing, rowChanges, selection]);

  React.useEffect(() => {
    // obtendo todos os ids das linhas da grid
    const rowIds = rows.map(row => getRowId(row));

    /**
     * Atualizo a lista de seleções para corresponder
     * apenas as linhas que estão sendo exibidas.
     */
    setSelection(currentValues => {
      return currentValues.filter(item => rowIds.includes(item));
    });
  }, [getRowId, rows]);

  return (
    <div className={classes.root}>
      <div className={classes.summary}>
        <div className={classes.buttonsActionsContainer}>
          <div className={classes.buttonsActionsContainerLeft}>
            <Typography variant="h6">Grupos</Typography>
          </div>
          <div className={classes.buttonsActionsContainerRight}>
            {/* Início divisão de ação de Editar */}
            <Hide
              hide={
                !checkPermissions([GROUPS.ADMINISTRATOR, GROUPS.PLANNING]) ||
                isEditing
              }
            >
              <Button
                color="primary"
                disabled={disableEditingButton}
                onClick={editingSelectedColumns}
              >
                Editar
              </Button>
            </Hide>

            <Hide
              hide={
                !checkPermissions([GROUPS.ADMINISTRATOR, GROUPS.PLANNING]) ||
                !isEditing
              }
            >
              <Button
                color="primary"
                disabled={!canSave}
                onClick={editingSelectedColumns}
              >
                Salvar
              </Button>
            </Hide>

            <Hide
              hide={
                !checkPermissions([GROUPS.ADMINISTRATOR, GROUPS.PLANNING]) ||
                !isEditing
              }
            >
              <Button
                color="primary"
                disabled={disableCancelButton}
                onClick={cancelEditingAction}
              >
                Cancelar
              </Button>
            </Hide>
            {/* Fim divisão da ação de editar */}
            <div className={classes.iconButton}>
              <IconButton onClick={onExport}>
                <Tooltip
                  title={
                    <span className={classes.tooltipLabel}>Exportar dados</span>
                  }
                >
                  <GetAppIcon color="primary" />
                </Tooltip>
              </IconButton>
            </div>
          </div>
        </div>
      </div>
      <Paper variant="elevation" elevation={0} className={classes.container}>
        <Grid columns={columns} rows={rows} getRowId={getRowId}>
          <SelectionState
            selection={selection}
            onSelectionChange={setSelection}
          />
          <IntegratedSelection />
          <CellDateTime />
          <CellUserGroup />
          <EditingState
            editingRowIds={editingRowIds}
            rowChanges={rowChanges}
            onRowChangesChange={newChangedRows => {
              onEdited(newChangedRows);
              setRowChanges(newChangedRows);
            }}
            onCommitChanges={commitChanges}
            columnExtensions={editingStateColumnExtensions}
          />
          <PagingState
            currentPage={currentPage}
            onCurrentPageChange={onCurrentPageChange}
            pageSize={pageSize}
            onPageSizeChange={onPageSizeChange}
          />
          <CustomPaging totalCount={totalCount} />
          <Table
            messages={tableMessages}
            cellComponent={props => {
              const { column, row } = props;
              const cellValue = row[column.name];
              if (cellValue) return <Table.Cell {...props} />;
              return <Table.Cell {...props}> - </Table.Cell>;
            }}
            columnExtensions={tableColumnExtensions}
          />
          <TableHeaderRow titleComponent={renderTitleComponent} />
          <TableEditRow
            rowComponent={RowEditingComponent}
            cellComponent={CellEditingComponent}
          />
          <TableSelection
            rowComponent={renderTableSelectionRowComponent}
            highlightRow
            showSelectAll
          />
          <PagingPanel messages={pagingPanelMessages} pageSizes={pageSizes} />
        </Grid>
        {(loading || updatingGridData) && <Loading />}
      </Paper>
    </div>
  );
};

export default GroupPermissionGrid;
